<script setup lang="ts">
import { useEventListener } from '@vueuse/core'
import { useAppStore } from '@/core/stores/useAppStore'
import {
  IconInfoCircle,
  IconCircleCheck,
  IconAlertTriangle,
  IconCircleX,
  IconX
} from '@tabler/icons-vue'

const appStore = useAppStore()

/**
 * Returns the appropriate icon component for notification type
 */
const getNotificationIcon = (type: 'info' | 'success' | 'warning' | 'error') => {
  switch (type) {
    case 'success':
      return IconCircleCheck
    case 'warning':
      return IconAlertTriangle
    case 'error':
      return IconCircleX
    default:
      return IconInfoCircle
  }
}

useEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    appStore.notifications.forEach((notification) => {
      appStore.removeNotification(notification.id)
    })
  }
})

/**
 * Vrátí CSS třídy pro notifikaci podle typu
 */
const getNotificationClasses = (
  type: 'info' | 'success' | 'warning' | 'error'
) => {
  const baseClasses = 'notification pointer-events-auto fg-card rounded-xl p-4 shadow-lg flex items-center border border-white/[0.06]'

  const typeClasses = {
    info: 'text-blue-400',
    success: 'text-green-400',
    warning: 'text-amber-400',
    error: 'text-red-400'
  }

  return `${baseClasses} ${typeClasses[type]}`
}
</script>

<template>
  <div
    class="fixed top-4 right-4 z-[100] space-y-2 w-full max-w-xs sm:max-w-md pointer-events-none"
  >
    <TransitionGroup 
      tag="div" 
      class="space-y-2"
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0 translate-x-8"
      enter-to-class="opacity-100 translate-x-0"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-8"
    >
      <div
        v-for="notification in appStore.notifications"
        :key="notification.id"
        :class="getNotificationClasses(notification.type)"
      >
        <div class="flex-shrink-0 mr-2">
          <component :is="getNotificationIcon(notification.type)" />
        </div>
        <div class="flex-1 mr-2 text-sm sm:text-base text-gray-800 dark:text-gray-100">
          {{ notification.message }}
        </div>
        <button
          class="text-gray-500 hover:text-gray-300 focus:outline-none rounded"
          @click="appStore.removeNotification(notification.id)"
        >
          <IconX class="w-4 h-4" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>
